/*
 * 模块: SKUInfoSection
 * 职责: SKU基础信息和统计摘要展示
 * 依赖: antd
 * 导出: SKUInfoSection
 * 限制: 文件<100行, 函数<30行
 */

import React from 'react';
import { Descriptions, Row, Col } from 'antd';
import {
  TrophyOutlined,
  SwapOutlined,
  RiseOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import type { SKU, SKUSummary } from '../../types';
import { StatsCard } from '../UI/Card/StatsCard';

interface SKUInfoSectionProps {
  sku: SKU;
  periodsCount: number;
  summary: SKUSummary | null;
}

/**
 * SKU信息区域组件
 * 展示SKU基础信息和统计摘要
 */
export const SKUInfoSection: React.FC<SKUInfoSectionProps> = ({
  sku,
  periodsCount,
  summary,
}) => {
  return (
    <div style={{ padding: '0 32px 24px' }}>
      {/* SKU信息卡片 */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: 16,
        padding: 24,
        border: '1px solid rgba(255, 255, 255, 0.6)',
        marginBottom: 32
      }}>
        <Descriptions column={3} labelStyle={{ color: '#64748b' }} contentStyle={{ color: '#0f172a', fontWeight: 500 }}>
          <Descriptions.Item label="ASIN">
            <span style={{ fontFamily: 'monospace', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{sku.asin}</span>
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{sku.created_at}</Descriptions.Item>
          <Descriptions.Item label="周期数量">
            <span style={{ color: '#4f46e5', fontWeight: 600 }}>{periodsCount}</span>
          </Descriptions.Item>
          <Descriptions.Item label="描述" span={3}>
            {sku.description || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>无描述</span>}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* 统计摘要卡片 */}
      {summary && (
        <Row gutter={[24, 24]} className="summary-cards">
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="平均市场控制度"
              value={Number(summary.avg_market_control.toFixed(1))}
              suffix="%"
              icon={<TrophyOutlined />}
              color="blue"
              description="表示在关键词搜索结果中的市场占有率"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="平均Top3替换率"
              value={Number(summary.avg_replacement_rate.toFixed(1))}
              suffix="%"
              icon={<SwapOutlined />}
              color="green"
              description="前3名产品被替换的平均概率"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="平均市场机会指数"
              value={Number(summary.avg_market_opportunity.toFixed(1))}
              suffix="%"
              icon={<RiseOutlined />}
              color="orange"
              description="市场中存在的潜在机会程度"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="关键词总数"
              value={summary.total_keywords}
              icon={<TagsOutlined />}
              color="purple"
              description="已分析的关键词总数量"
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

